<template>
    <div class="page__content availabilities-intro">
        <div class="columns">
            <div class="column is-8">
                <h2 class="page__headline">{{ $t('availabilities.intro.headline') }}</h2>
                <p class="page__copy" v-if="!readOnly">{{ $t('availabilities.intro.copy') }}</p>
                <p class="page__copy" v-else>{{ $t('availabilities.intro.copyReadOnly') }}</p>
            </div>
        </div>

        <section class="availabilities__section">
            <div class="edit-box box-shadow">
                <div class="availabilities__wrapper--inner">
                    <div class="columns">
                        <div class="column">
                            <h3>{{ $t('availabilities.intro.headline2') }}</h3>
                            <div class="notification is-yellow" v-if="!readOnly" v-html="$t('availabilities.intro.copy2')"></div>
                            <div class="notification is-yellow" v-else v-html="$t('availabilities.intro.copy2ReadOnly')"></div>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column">
                            <availabilities-year-calendar :year="year" :loading="loading" :availabilities="availabilitiesByMonth"></availabilities-year-calendar>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="availabilities__section changed-availabilities-section" v-if="!readOnly">
            <div class="edit-box box-shadow">
                <div class="availabilities__wrapper--inner">
                    <div class="columns">
                        <div class="column">
                            <h3>{{ $t('availabilities.intro.headline3') }}</h3>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column">
                            <changed-availabilities :page="changedAvailabilitiesPageFromQuery" :ordering="changedAvailabilitiesOrderingFromQuery" :campsite="changedAvailabilitiesCampsiteFromQuery" :dateFrom="changedAvailabilitiesDateFromFromQuery" :dateUntil="changedAvailabilitiesDateUntilFromQuery" :campsites="campsites"></changed-availabilities>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="availabilities__section changed-availabilities-section" v-else>
            <div class="edit-box box-shadow">
                <div class="availabilities__wrapper--inner">
                    <div class="columns">
                        <div class="column">
                            <h3><i class="fa fa-exclamation-triangle is-danger" aria-hidden="true" v-if="this.lastApicallWarningLevel > 0"></i> {{ $t('availabilities.intro.headline4') }}</h3>
                        </div>
                    </div>

                    <div class="columns last-api-call">
                        <div class="column">
                            <div class="notification is-yellow" v-if="!lastApiCall">
                                {{ $t('availabilities.intro.copy4') }}
                            </div>

                            <div v-else class="columns">
                                <div class="column is-5">
                                    <table class="table is-bordered">
                                        <tbody>
                                            <tr>
                                                <td class="is-narrow head">{{ $t('availabilities.infoTable.dateAndTime') }}:</td>
                                                <td>
                                                    <span class="tag">
                                                        {{ lastApiCall.date | localizedDate($t('generic.calendar.dateformatMomentjsFull')) }}
                                                        {{ lastApiCall.date | localizedDate($t('generic.calendar.timeformatMomentjsFull')) }}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td class="is-narrow head">{{ $t('availabilities.infoTable.timePassedSince') }}:</td>
                                                <td v-html="lastApicallErrorTimeSince"></td>
                                            </tr>
                                            <tr>
                                                <td class="is-narrow head">{{ $t('availabilities.infoTable.state') }}:</td>
                                                <td v-html="lastApicallError"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
    import { Api } from '../../../api';
    import { AppModel } from '../../../models';
    import { AvailabilitiesYearCalendar, ChangedAvailabilities } from '../../components';
    import filters from '../../../filters';
    import moment from 'moment';

    export default {
        name: 'availabilities-intro',

        components: {
            AvailabilitiesYearCalendar,
            ChangedAvailabilities,
        },

        filters: filters,

        props: {
            year: {
                type: Number,
                'default': null,
            },
        },

        data() {
            return {
                campsites: [],
                readOnly: true,
                availabilitiesByMonth: [],
                loading: true,
                changedAvailabilitiesPage: 1,
                lastApiCall: null,
                filters: filters,
                lastApicallErrorTimeSince: null,
                lastApicallErrorTimeSinceInterval: null,
            };
        },

        computed: {
            changedAvailabilitiesPageFromQuery() {
                return Number(this.$route.query.page) || 1;
            },
            changedAvailabilitiesOrderingFromQuery() {
                return this.$route.query.ordering || 'day';
            },
            changedAvailabilitiesCampsiteFromQuery() {
                return this.$route.query.campsite || null;
            },
            changedAvailabilitiesDateFromFromQuery() {
                return this.$route.query.date_from || null;
            },
            changedAvailabilitiesDateUntilFromQuery() {
                return this.$route.query.date_until || null;
            },
            lastApicallError() {
                if(!this.lastApiCall) {
                    return '';
                }

                if(this.lastApiCall.error) {
                    return '<span class="tag is-danger">' + this.$t('availabilities.intro.erroneous') + '</span>';
                } else {
                    return '<span class="tag is-success">' + this.$t('availabilities.intro.successful') + '</span>';
                }
            },
            lastApicallWarningLevel() {
                if(!this.lastApiCall || !this.lastApiCall.date) {
                    return 0;
                }
                const diff = moment().diff(this.lastApiCall.date) / 3600000;
                let level = 0;
                if(diff > 5) {
                    level = 1;
                }
                if(diff >= 48) {
                    level = 2;
                }
                return level;
            },
        },

        beforeRouteEnter(to, from, next) {
            Api.bootstrap().then(function(response) {
                return Promise.all([response, Api.campsites.fetchCampsites(), Api.availabilities.fetchAvailabilities({'year': to.params.year})]);
            }).then(function(response) {
                next((vm) => {
                    vm.readOnly = AppModel.activeCampground.settings.pms_api_active;
                    vm.lastApiCall = AppModel.activeCampground.last_api_call;
                    vm.campsites = response[1].body;
                    let idx = 0;
                    vm.availabilitiesByMonth = response[2].body.map((a) => {
                        a.idx = idx++;
                        return a;
                    });
                    vm.setLastApicallErrorTimeSince(vm.lastApiCall && vm.readOnly);
                    vm.loading = false;
                });
            });
        },

        beforeRouteUpdate(to, from, next) {
            if(Number(to.params.year) !== Number(this.year)) {
                this.loading = true;
                Api.availabilities.fetchAvailabilities({'year': to.params.year}).then((response) => {
                    next();
                    this.availabilitiesByMonth = response.body;
                    this.loading = false;
                });
            } else {
                next();
            }
        },

        beforeRouteLeave(to, f, next) {
            clearInterval(this.lastApicallErrorTimeSinceInterval);
            next();
        },

        methods: {
            setLastApicallErrorTimeSince(createTimeout = false) {
                if(!this.lastApiCall || !this.lastApiCall.date) {
                    return;
                }

                const level = this.lastApicallWarningLevel;
                const status = ['is-success', 'is-warning', 'is-danger'];

                let timeSince = moment(this.lastApiCall.date).fromNow(true);
                this.lastApicallErrorTimeSince = `<span class="tag ${status[level]}">${timeSince}</span>`;
                if(createTimeout) {
                    this.lastApicallErrorTimeSinceInterval = setInterval(() => {
                        this.setLastApicallErrorTimeSince();
                    }, 5);
                }
            },
        },
    };
</script>

<style lang="scss">
    @import './availabilities-intro.scss';
</style>



// WEBPACK FOOTER //
// src/views/pages/availabilities/availabilities-intro.vue